import api from 'services/api';
import { REJECT, array, nullable, num, object, str, type Received } from 'validation';
import { readResponse } from 'api/shared/readResponse';

import { parameterSchema, type Parameter } from './types';

// A PROJECTION CONTRACT over `GET /node_definitions/:id` — see ../../README.md. The full contract for this route is
// `getNodeDefinition`; this one declares what the Flow side panel reads about the definition a selected placement refers
// to. The panel shows the definition's values as the defaults a placement overrides, and edits only the placement.
//
// Separate from `getNodeDefinitionGraph` on purpose: drift is reported for what the panel shows, not for the graph of a
// definition it never draws. The panel takes a degraded answer, since it only shows this record; the definition's own
// editor (`getNodeDefinition`) takes one too.

/** One output handle, as the side panel lists it. Narrower than the shared `Handle`: the panel reads no condition. */
export interface PlacedNodeDefinitionHandle {
  readonly id: string;
  readonly label: string;
}

/** A node definition as the Flow side panel shows it for a placement. */
export interface PlacedNodeDefinition {
  readonly node_definition_id: number;
  // Stricter than the nullable column, as in `getNodeDefinition` (see the note there).
  readonly name: string;
  readonly description: string | null;
  // "queueZone|queueName", or null.
  readonly default_queue_path: string | null;
  readonly parameters: readonly Parameter[];
  readonly handles: readonly PlacedNodeDefinitionHandle[];
  // "zone|lock" tag strings.
  readonly locks: readonly string[];
}

const placedNodeDefinitionSchema = object<PlacedNodeDefinition>({
  node_definition_id: num(REJECT),
  name: str(REJECT),
  description: nullable(str()),
  default_queue_path: nullable(str()),
  parameters: array(parameterSchema),
  handles: array(object<PlacedNodeDefinitionHandle>({ id: str(REJECT), label: str() })),
  locks: array(str()),
});

/**
 * Definition `id`, as the side panel shows it. A missing definition is not a 404 here: the backend answers it with a 500,
 * so it rejects like any other failed call.
 */
export const getPlacedNodeDefinition = async (id: number): Promise<Received<PlacedNodeDefinition>> => {
  const res = await api.get(`/node_definitions/${id}`);
  return readResponse(res.data, { schema: placedNodeDefinitionSchema, source: 'getPlacedNodeDefinition' });
};
